import React, { useEffect, useRef, useState } from 'react';
import VoiceWaveform from './VoiceWaveform';
import { TrainingApi, AskQuestionResponse } from './trainingApi';

type DialogStage = 'intro' | 'recording' | 'transcribing' | 'answer' | 'error';

interface QuestionDialogProps {
  lessonId: string;
  currentTimeSec: number;
  onClose: () => void;
  onResume: () => void;
}

const pickRecorderMimeType = (): string => {
  if (typeof MediaRecorder === 'undefined') return '';
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];
  for (const type of candidates) {
    if ((MediaRecorder as any).isTypeSupported && (MediaRecorder as any).isTypeSupported(type)) {
      return type;
    }
  }
  return '';
};

const base64ToBlob = (base64: string, mimeType: string): Blob => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mimeType });
};

const QuestionDialog: React.FC<QuestionDialogProps> = ({ lessonId, currentTimeSec, onClose, onResume }) => {
  const [stage, setStage] = useState<DialogStage>('intro');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [micDenied, setMicDenied] = useState<boolean>(false);
  const [textQuestion, setTextQuestion] = useState<string>('');
  const [response, setResponse] = useState<AskQuestionResponse | null>(null);
  const [isAnswerPlaying, setIsAnswerPlaying] = useState<boolean>(false);
  const [answerAudioSrc, setAnswerAudioSrc] = useState<string | null>(null);
  const [answerAudioError, setAnswerAudioError] = useState<string | null>(null);
  const [answerMediaStream, setAnswerMediaStream] = useState<MediaStream | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const recorderChunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const [activeStream, setActiveStream] = useState<MediaStream | null>(null);
  const answerAudioRef = useRef<HTMLAudioElement | null>(null);
  const answerObjectUrlRef = useRef<string | null>(null);

  const captureAnswerStream = (audio: HTMLAudioElement) => {
    const audioWithCapture = audio as HTMLAudioElement & {
      captureStream?: () => MediaStream;
      mozCaptureStream?: () => MediaStream;
    };
    const capture = audioWithCapture.captureStream || audioWithCapture.mozCaptureStream;
    if (!capture) {
      setAnswerMediaStream(null);
      return;
    }
    setAnswerMediaStream(capture.call(audio));
  };

  useEffect(() => () => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      try { recorderRef.current.stop(); } catch (_) { /* noop */ }
    }
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (answerAudioRef.current) {
      answerAudioRef.current.pause();
      answerAudioRef.current.removeAttribute('src');
      answerAudioRef.current.load();
    }
    if (answerObjectUrlRef.current) {
      URL.revokeObjectURL(answerObjectUrlRef.current);
      answerObjectUrlRef.current = null;
    }
  }, []);

  useEffect(() => {
    const audio = answerAudioRef.current;
    if (!audio || !answerAudioSrc) return;

    setAnswerAudioError(null);
    setIsAnswerPlaying(false);
    setAnswerMediaStream(null);
    audio.muted = false;
    audio.volume = 1;
    audio.currentTime = 0;
    audio.load();

    const timer = window.setTimeout(() => {
      audio.play().catch((err) => {
        console.warn('Soru cevabı sesi otomatik başlatılamadı:', err);
        setIsAnswerPlaying(false);
        setAnswerAudioError('Cevap sesi otomatik başlatılamadı. Tekrar dinle düğmesine bas.');
      });
    }, 0);

    return () => window.clearTimeout(timer);
  }, [answerAudioSrc]);

  const startRecording = async () => {
    setErrorMessage(null);
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setMicDenied(true);
      setStage('intro');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      setActiveStream(stream);
      const mimeType = pickRecorderMimeType();
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      recorderChunksRef.current = [];
      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) recorderChunksRef.current.push(event.data);
      };
      recorder.onstop = async () => {
        const recorderMime = recorder.mimeType || mimeType || 'audio/webm';
        const blob = new Blob(recorderChunksRef.current, { type: recorderMime });
        stream.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
        setActiveStream(null);
        await submitQuestion({ audio: blob, mimeType: recorderMime });
      };
      recorder.start();
      recorderRef.current = recorder;
      setStage('recording');
    } catch (err: any) {
      console.warn('Mikrofon izni alınamadı:', err);
      setMicDenied(true);
      setStage('intro');
    }
  };

  const stopRecording = () => {
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      try { recorder.stop(); } catch (_) { /* noop */ }
    }
    setStage('transcribing');
  };

  const submitQuestion = async (input: { audio?: Blob; mimeType?: string; question?: string }) => {
    setStage('transcribing');
    setErrorMessage(null);
    try {
      const data = await TrainingApi.ask(lessonId, {
        audio: input.audio,
        mimeType: input.mimeType,
        question: input.question,
        currentTimeSec,
      });
      setResponse(data);
      setStage('answer');
      if (answerAudioRef.current) {
        answerAudioRef.current.pause();
        answerAudioRef.current.currentTime = 0;
      }
      setAnswerMediaStream(null);
      if (answerObjectUrlRef.current) {
        URL.revokeObjectURL(answerObjectUrlRef.current);
        answerObjectUrlRef.current = null;
      }
      const blob = base64ToBlob(data.audioBase64, data.mimeType || 'audio/wav');
      const url = URL.createObjectURL(blob);
      answerObjectUrlRef.current = url;
      setAnswerAudioError(null);
      setAnswerAudioSrc(url);
    } catch (err: any) {
      setErrorMessage(err?.message || 'Cevap alınamadı');
      setStage('error');
    }
  };

  const submitText = async () => {
    const trimmed = textQuestion.trim();
    if (!trimmed) return;
    await submitQuestion({ question: trimmed });
  };

  const replayAnswer = () => {
    const audio = answerAudioRef.current;
    if (!audio || !answerAudioSrc) return;
    setAnswerAudioError(null);
    audio.muted = false;
    audio.volume = 1;
    audio.currentTime = 0;
    audio.play().catch((err) => {
      console.warn('Soru cevabı sesi oynatılamadı:', err);
      setIsAnswerPlaying(false);
      setAnswerAudioError('Cevap sesi oynatılamadı.');
    });
  };

  const handleClose = () => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      try { recorderRef.current.stop(); } catch (_) { /* noop */ }
    }
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    setActiveStream(null);
    if (answerAudioRef.current) {
      answerAudioRef.current.pause();
      answerAudioRef.current.currentTime = 0;
    }
    setAnswerMediaStream(null);
    onResume();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-[#0b1f29]/55 backdrop-blur-sm sm:items-center">
      <div className="w-full max-w-2xl rounded-t-3xl bg-white shadow-[0_-20px_60px_rgba(11,31,41,0.25)] sm:rounded-3xl sm:shadow-[0_30px_80px_rgba(11,31,41,0.25)]">
        <audio
          ref={answerAudioRef}
          src={answerAudioSrc || undefined}
          preload="auto"
          className="hidden"
          onPlay={() => {
            setAnswerAudioError(null);
            if (answerAudioRef.current) captureAnswerStream(answerAudioRef.current);
            setIsAnswerPlaying(true);
          }}
          onPause={() => setIsAnswerPlaying(false)}
          onEnded={() => {
            setIsAnswerPlaying(false);
          }}
          onError={() => {
            setIsAnswerPlaying(false);
            setAnswerMediaStream(null);
            if (answerAudioSrc) setAnswerAudioError('Cevap sesi yüklenemedi veya oynatılamadı.');
          }}
        />
        <div className="flex items-center justify-between border-b border-[#ecf2f5] px-6 py-4">
          <div>
            <h3 className="text-lg font-extrabold tracking-tight text-[#174e67]">Sormak istediğin var mı?</h3>
            <p className="mt-1 text-xs text-[#67808c]">Ders dakikası: {Math.floor(currentTimeSec / 60)}.{String(Math.floor(currentTimeSec % 60)).padStart(2, '0')}</p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="rounded-full p-2 text-[#67808c] transition hover:bg-[#f4f7f8] hover:text-[#174e67]"
            aria-label="Kapat"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
          </button>
        </div>

        <div className="px-6 py-7">
          {stage === 'intro' && (
            <div className="flex flex-col items-center gap-5 text-center">
              {!micDenied ? (
                <>
                  <p className="max-w-sm text-sm leading-6 text-[#5d7681]">Mikrofona dokun ve sorunu konuşarak sor. Cevap verirken aklında kalan ya da daha derinine inmek istediğin bir şey olduğunda dersi durdurabilirsin.</p>
                  <button
                    type="button"
                    onClick={startRecording}
                    className="group relative flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-[#0ea5b7] to-[#174e67] text-white shadow-[0_18px_45px_rgba(14,165,183,0.45)] transition active:scale-95"
                    aria-label="Konuşmaya başla"
                  >
                    <span className="absolute inset-0 animate-ping rounded-full bg-[#0ea5b7]/30" />
                    <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" x2="12" y1="19" y2="22"/></svg>
                  </button>
                  <p className="text-xs text-[#8a9ba6]">veya</p>
                </>
              ) : (
                <p className="rounded-2xl bg-[#fef4f4] px-4 py-3 text-sm text-[#a04c4c]">Mikrofon iznine ulaşılamıyor. Sorunu yazarak gönderebilirsin.</p>
              )}
              <div className="w-full">
                <textarea
                  value={textQuestion}
                  onChange={(event) => setTextQuestion(event.target.value)}
                  placeholder="Sorunu buraya yazabilirsin"
                  rows={3}
                  className="w-full resize-none rounded-2xl border border-[#dbe7ec] bg-[#f7fbfc] px-4 py-3 text-sm text-[#1d4758] outline-none focus:border-[#0ea5b7] focus:bg-white"
                />
                <button
                  type="button"
                  onClick={submitText}
                  disabled={!textQuestion.trim()}
                  className="mt-3 w-full rounded-2xl bg-[#174e67] px-4 py-3 text-sm font-bold text-white transition hover:bg-[#0d3a4f] disabled:cursor-not-allowed disabled:bg-[#bfd1d8]"
                >
                  Soruyu yazarak gönder
                </button>
              </div>
            </div>
          )}

          {stage === 'recording' && (
            <div className="flex flex-col items-center gap-5">
              <div className="h-28 w-full overflow-hidden rounded-2xl bg-[#0b1f29]">
                <VoiceWaveform mediaStream={activeStream} isActive className="text-white" barCount={28} />
              </div>
              <p className="text-sm text-[#5d7681]">Seni dinliyorum, soruyu söylediğinde aşağıdaki butona dokun.</p>
              <button
                type="button"
                onClick={stopRecording}
                className="rounded-full bg-[#174e67] px-7 py-3 text-sm font-bold text-white shadow-lg shadow-[#174e67]/25 transition active:scale-95"
              >
                Sorum bitti
              </button>
            </div>
          )}

          {stage === 'transcribing' && (
            <div className="flex flex-col items-center gap-3 py-6 text-center">
              <div className="h-10 w-10 animate-spin rounded-full border-[3px] border-[#0ea5b7] border-t-transparent" />
              <p className="text-sm text-[#5d7681]">Sorunu işliyorum, bir saniye…</p>
            </div>
          )}

          {stage === 'answer' && response && (
            <div className="flex flex-col gap-4">
              <div className="rounded-2xl border border-[#dbe7ec] bg-[#f7fbfc] px-4 py-3">
                <p className="text-[11px] font-bold uppercase tracking-wide text-[#67808c]">Senin sorun</p>
                <p className="mt-1 text-sm text-[#1d4758]">{response.question}</p>
              </div>
              <div className="rounded-2xl bg-gradient-to-br from-[#e9f6f8] to-white px-4 py-4">
                <p className="text-[11px] font-bold uppercase tracking-wide text-[#0ea5b7]">Eğitmen cevabı</p>
                <div className="mt-3 h-36 overflow-hidden rounded-2xl bg-[#0b1f29] sm:h-44">
                  <VoiceWaveform mediaStream={answerMediaStream} isActive={isAnswerPlaying} barCount={44} />
                </div>
                <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
                  <button
                    type="button"
                    onClick={replayAnswer}
                    disabled={!answerAudioSrc}
                    className="inline-flex items-center gap-2 text-xs font-bold text-[#174e67] transition hover:text-[#0ea5b7] disabled:cursor-not-allowed disabled:text-[#9fb3bc]"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 1 0 9-9"/><path d="M3 4v5h5"/></svg>
                    {isAnswerPlaying ? 'Ses çalıyor' : 'Tekrar dinle'}
                  </button>
                  {answerAudioError ? (
                    <span className="text-xs font-semibold text-[#a04c4c]">{answerAudioError}</span>
                  ) : null}
                </div>
              </div>
              <button
                type="button"
                onClick={handleClose}
                className="w-full rounded-2xl bg-[#174e67] px-4 py-3 text-sm font-bold text-white transition hover:bg-[#0d3a4f]"
              >
                Tamam, anladım - derse devam et
              </button>
            </div>
          )}

          {stage === 'error' && (
            <div className="flex flex-col gap-4 text-center">
              <p className="rounded-2xl bg-[#fef4f4] px-4 py-3 text-sm text-[#a04c4c]">{errorMessage || 'Cevap alınamadı'}</p>
              <button
                type="button"
                onClick={() => { setStage('intro'); setErrorMessage(null); }}
                className="rounded-2xl bg-[#174e67] px-4 py-3 text-sm font-bold text-white"
              >
                Tekrar dener misin?
              </button>
              <button
                type="button"
                onClick={handleClose}
                className="text-sm font-medium text-[#67808c] hover:text-[#174e67]"
              >
                Kapat ve derse dön
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuestionDialog;
